import { useEffect, useReducer, useRef } from 'react';
import { initialConversationState } from '../utils/conversationFlow';
import {
  clearPersistedSessionId,
  generateSessionId,
  getSessionScope,
  loadOrCreateSessionId,
  persistSessionId,
} from '../utils/storage';

// The session id is the key of the lead row in Drupal, so it must survive a
// refresh and be shared by a second tab — minting one per mount is what turned
// one prospect into several leads. Resume the persisted id for this community
// when there is a live one; mint only when there is not.
const createInitialState = (scope) => ({
  messages: [],
  conversationState: initialConversationState,
  flowStep: 1,
  sessionId: loadOrCreateSessionId(scope),
  interestCategories: [],
  interestCategoriesLoading: false,
  amenityPlaces: [],
  pendingPlaces: [],
  pendingPlacesInterests: '',
  isFormSubmitted: false,
  additionalInterests: '',
  isAiMode: false,
  isAiThinking: false,
  aiThinkingWithLabel: false,
  isClosureFlowActive: false,
  isSessionEnded: false,
  hasShownFollowUp: false,
});

const tourGuideReducer = (state, action) => {
  switch (action.type) {
    case 'SET_MESSAGES': {
      // Preserve debug delay tag across rebuilds: callers often re-construct
      // messages from a locally-captured array that predates UPDATE_LAST_MESSAGE.
      const existing = new Map(state.messages.map((m) => [m.id, m]));
      const merged = action.payload.map((m) => {
        const prev = existing.get(m.id);
        if (prev && prev.nextDelayMs && !m.nextDelayMs) {
          return {
            ...m,
            nextDelayMs: prev.nextDelayMs,
            nextDelayDeadline: prev.nextDelayDeadline,
          };
        }
        return m;
      });
      // When a brand-new follow-up prompt enters the list (e.g. AI text
      // response with inlined next_step buttons), clear selectedFollowUpAction
      // so its buttons don't render as carried-over selections from the
      // previous turn. Mirrors the APPEND_MESSAGES reset.
      const hasNewFollowUpPrompt = action.payload.some(
        (m) => (m.stepType === 'next_step_prompt' || m.stepType === 'more_places_prompt')
          && !existing.has(m.id),
      );
      const conversationState = hasNewFollowUpPrompt
        ? { ...state.conversationState, selectedFollowUpAction: null }
        : state.conversationState;
      return { ...state, messages: merged, conversationState };
    }

    case 'APPEND_MESSAGES': {
      const tagged = action.deferred
        ? action.payload.map((m) => ({ ...m, deferred: true }))
        : action.payload;
      const newMessages = [...state.messages, ...tagged];
      const hasFollowUpPrompt = action.payload.some(
        (m) => m.stepType === 'next_step_prompt' || m.stepType === 'more_places_prompt',
      );
      const updatedConvState = {
        ...state.conversationState,
        visitedExternalLocations: [],
        ...(hasFollowUpPrompt && { selectedFollowUpAction: null }),
      };
      return { ...state, messages: newMessages, conversationState: updatedConvState };
    }

    case 'UPDATE_LAST_MESSAGE':
      if (state.messages.length === 0) return state;
      return {
        ...state,
        messages: state.messages.map((msg, i) => {
          const isLastMsg = i === state.messages.length - 1;
          return isLastMsg ? { ...msg, ...action.payload } : msg;
        }),
      };

    // Patch one message by id. UPDATE_LAST_MESSAGE is the usual tool, but the
    // brochure link lands on the goodbye message whenever the network says so,
    // by which time "last" is no longer guaranteed to mean "that one".
    case 'UPDATE_MESSAGE_BY_ID':
      return {
        ...state,
        messages: state.messages.map((msg) => (
          msg.id === action.payload.id ? { ...msg, ...action.payload.changes } : msg
        )),
      };

    case 'SET_CONVERSATION_STATE':
      return {
        ...state,
        conversationState: typeof action.payload === 'function'
          ? action.payload(state.conversationState)
          : { ...state.conversationState, ...action.payload },
      };

    case 'REPLACE_CONVERSATION_STATE':
      return { ...state, conversationState: action.payload };

    case 'SET_FLOW_STEP':
      return { ...state, flowStep: action.payload };

    case 'SET_IS_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_SESSION_ID':
      return { ...state, sessionId: action.payload };

    case 'SET_INTEREST_CATEGORIES':
      return { ...state, interestCategories: action.payload, interestCategoriesLoading: false };

    case 'SET_INTEREST_CATEGORIES_LOADING':
      return { ...state, interestCategoriesLoading: action.payload };

    case 'SET_AMENITY_PLACES':
      return { ...state, amenityPlaces: action.payload };

    case 'APPEND_AMENITY_PLACES': {
      const existingIds = state.amenityPlaces.map((p) => p.id);
      const newPlaces = action.payload.filter((p) => !existingIds.includes(p.id));
      return { ...state, amenityPlaces: [...state.amenityPlaces, ...newPlaces] };
    }

    case 'SET_PENDING_PLACES':
      return {
        ...state,
        pendingPlaces: action.payload.places,
        pendingPlacesInterests: action.payload.interests || state.pendingPlacesInterests,
      };

    case 'CLEAR_PENDING_PLACES':
      return { ...state, pendingPlaces: [], pendingPlacesInterests: '' };

    case 'SET_IS_FORM_SUBMITTED':
      return { ...state, isFormSubmitted: action.payload };

    case 'SET_ADDITIONAL_INTERESTS':
      return { ...state, additionalInterests: action.payload };

    case 'SET_IS_AI_MODE':
      return { ...state, isAiMode: action.payload };

    case 'SET_IS_AI_THINKING': {
      const p = action.payload;
      if (typeof p === 'object' && p !== null) {
        return { ...state, isAiThinking: !!p.active, aiThinkingWithLabel: !!p.withLabel };
      }
      return { ...state, isAiThinking: !!p, aiThinkingWithLabel: false };
    }

    case 'SET_IS_CLOSURE_FLOW_ACTIVE':
      return { ...state, isClosureFlowActive: action.payload };

    case 'SET_IS_SESSION_ENDED':
      return { ...state, isSessionEnded: action.payload };

    case 'SET_HAS_SHOWN_FOLLOW_UP':
      return { ...state, hasShownFollowUp: action.payload };

    case 'END_SESSION':
      return {
        ...state,
        messages: [...state.messages, ...action.payload],
        isSessionEnded: true,
        isClosureFlowActive: false,
      };

    case 'RESET_SESSION':
      return {
        ...state,
        sessionId: generateSessionId(),
        messages: [],
        conversationState: {
          currentStep: 'welcome_form',
          selectedInterests: [],
          selectedAmenities: [],
          visitedAmenities: [],
          userInfo: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            previousSession: false,
          },
          conversationData: {},
          formData: {},
          formErrors: {},
        },
        interestCategories: state.interestCategories,
        interestCategoriesLoading: false,
        amenityPlaces: [],
        pendingPlaces: [],
        pendingPlacesInterests: '',
        isFormSubmitted: false,
        additionalInterests: '',
        isAiMode: false,
        isAiThinking: false,
        aiThinkingWithLabel: false,
        isClosureFlowActive: false,
        isSessionEnded: false,
        flowStep: 1,
        isLoading: false,
        hasShownFollowUp: false,
      };

    default:
      return state;
  }
};

const useTourGuideState = (scopeOverride) => {
  // Pinned for the life of the component: the scope must not change under a
  // running conversation, or the id would be re-persisted under a new key.
  const scopeRef = useRef(scopeOverride === undefined ? getSessionScope() : scopeOverride);
  const scope = scopeRef.current;

  const [state, dispatch] = useReducer(tourGuideReducer, scope, createInitialState);

  // Keep the persisted id in step with the live one, and refresh its timestamp
  // as the conversation moves so a long tour does not idle out mid-visit. An
  // ended session is cleared: the next visit is a genuinely new lead visit.
  const { sessionId, isSessionEnded, messages } = state;
  useEffect(() => {
    if (isSessionEnded) {
      clearPersistedSessionId(scope);
      return;
    }
    persistSessionId(sessionId, scope);
  }, [scope, sessionId, isSessionEnded, messages.length]);

  return { state, dispatch };
};

export default useTourGuideState;
